import { ReactElement, useContext, useEffect, useState } from 'react'
import { ModalDialog } from './ModalDialog'
import { Box, Button, DialogActions, DialogContent, Stack, Typography } from '@mui/material'
import { SessionTimeoutContext, totalCountdownSeconds } from 'src/user/SessionTimeoutContext'
import { useMessageSource } from 'src/i18n/useMessageSource'

export const SessionTimeoutModalDialog = (): ReactElement => {
  const { getMessage } = useMessageSource()
  const { sessionTimeoutModal, manualExtendSession, manualLogout } = useContext(SessionTimeoutContext)

  const [timeLeft, setTimeLeft] = useState(totalCountdownSeconds)
  const minutes = Math.floor(timeLeft / 60)
  const seconds = String(timeLeft % 60)

  useEffect(() => {
    if (!sessionTimeoutModal || sessionTimeoutModal.typeOfSessionModal === 'expired') {
      return
    }

    const endTime = Date.now() + totalCountdownSeconds * 1000

    const countdownInterval = setInterval(() => {
      const secondsRemaining = Math.max(0, Math.floor((endTime - Date.now()) / 1000))
      setTimeLeft(secondsRemaining)
      if (secondsRemaining <= 0) {
        // explicitly logout user after the timer ends
        clearInterval(countdownInterval)
        manualLogout()
      }
    }, 250)

    return () => {
      clearInterval(countdownInterval)
    }
  }, [manualLogout, sessionTimeoutModal])

  return (
    <>
      {(() => {
        switch (sessionTimeoutModal?.typeOfSessionModal) {
          case 'expired':
            return (
              <>
                <ModalDialog
                  open={Boolean(sessionTimeoutModal)}
                  onClose={manualLogout}
                  title={getMessage('label.session.expired.modal.title')}
                  maxWidth="sm"
                  withCloseIcon={false}
                >
                  <DialogContent>
                    <Stack spacing={2}>
                      <Typography>{getMessage('label.session.expired.modal.content')}</Typography>
                    </Stack>
                  </DialogContent>
                </ModalDialog>
              </>
            )
          case 'warning':
            return (
              <>
                <ModalDialog
                  open={Boolean(sessionTimeoutModal)}
                  onClose={manualLogout}
                  title={getMessage('label.session.timeout.modal.title')}
                  maxWidth="sm"
                  withCloseIcon={false}
                >
                  <DialogContent>
                    <Stack spacing={2}>
                      <Typography>{getMessage('label.session.timeout.modal.content.top')}</Typography>
                      <Typography variant="h2">
                        <Box component="span" fontWeight="bold" mr={1}>
                          {minutes}
                        </Box>
                        <Box component="span" mr={1}>
                          {minutes !== 1
                            ? getMessage('label.session.timeout.modal.timer.minutes')
                            : getMessage('label.session.timeout.modal.timer.minute')}
                        </Box>
                        <Box component="span" fontWeight="bold" mr={1}>
                          {seconds}
                        </Box>
                        <Box component="span">{getMessage('label.session.timeout.modal.timer.seconds')}</Box>
                      </Typography>
                      <Typography fontWeight="bold">
                        {getMessage('label.session.timeout.modal.content.bottom')}
                      </Typography>
                      <Box>
                        <Typography>
                          {getMessage('label.session.timeout.modal.continue.button')} -{' '}
                          {getMessage('label.session.timeout.modal.continue.explanation')}
                        </Typography>
                        <Typography>
                          {getMessage('label.session.timeout.modal.logout.button')} -{' '}
                          {getMessage('label.session.timeout.modal.logout.explanation')}
                        </Typography>
                      </Box>
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button variant="text" onClick={manualLogout}>
                      {getMessage('label.session.timeout.modal.logout.button')}
                    </Button>
                    <Button variant="contained" onClick={manualExtendSession}>
                      {getMessage('label.session.timeout.modal.continue.button')}
                    </Button>
                  </DialogActions>
                </ModalDialog>
              </>
            )
          default:
            return <></>
        }
      })()}
    </>
  )
}
